import React from 'react';
import { Link } from 'react-router-dom';
import { KATEGORIER } from './CategorySelector';
import { getFylkeName, getByName } from '../data/norwegianCities';

const JobCard = ({ job, isMobileLayout = false }) => {
  const kategori = KATEGORIER.find(k => k.id === job.category);
  const locationText = job.location_city 
    ? getByName(job.location_city)
    : getFylkeName(job.location_fylke);

  // Format price display
  const getPriceDisplay = () => {
    if (job.pricing_type === 'hourly') {
      return `${(job.hourly_rate || 0).toLocaleString('nb-NO')} kr/t`;
    } else if (job.pricing_type === 'negotiable') {
      return 'Etter avtale';
    }
    return `${(job.price || 0).toLocaleString('nb-NO')} kr`;
  };

  // Mobil-layout: Horisontalt kort med bilde på venstre side
  if (isMobileLayout) {
    return (
      <Link 
        to={`/annonse/${job.id}`}
        className="flex bg-white border-b border-slate-100 active:bg-slate-50 transition-colors"
      >
        {/* Bilde - venstre side */}
        <div className="w-28 h-28 flex-shrink-0 relative">
          {job.images && job.images.length > 0 ? (
            <img 
              src={job.images[0].startsWith('http') ? job.images[0] : `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${job.images[0]}`} 
              alt={job.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#1E3A5F] to-[#0A1929] flex items-center justify-center">
              <span className="material-symbols-outlined text-white/30 text-3xl">
                {kategori?.icon || 'work'}
              </span>
            </div>
          )}
          {/* Type badge */}
          {job.job_type && (
            <div className={`absolute top-1.5 left-1.5 text-[10px] font-medium px-1.5 py-0.5 rounded ${
              job.job_type === 'tjeneste' 
                ? 'bg-purple-500 text-white' 
                : 'bg-blue-500 text-white'
            }`}>
              {job.job_type === 'tjeneste' ? 'Tjeneste' : 'Oppdrag'}
            </div>
          )}
        </div>

        {/* Innhold - høyre side */}
        <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
          <div>
            {/* Tittel */}
            <h3 className="font-heading font-semibold text-[#0A1929] text-sm leading-tight line-clamp-2">
              {job.title}
            </h3>
            
            {/* Lokasjon og kategori */}
            <div className="flex items-center gap-2 mt-1.5 text-xs text-slate-500">
              <span className="flex items-center gap-0.5">
                <span className="material-symbols-outlined text-sm">location_on</span>
                {locationText}
              </span>
              <span>•</span>
              <span className="flex items-center gap-0.5">
                <span className="material-symbols-outlined text-sm">{kategori?.icon || 'work'}</span>
                {kategori?.name || job.category}
              </span>
            </div>
          </div>

          {/* Pris */}
          <div className="flex items-center justify-between mt-2">
            <span className="font-heading font-bold text-[#1E3A5F] text-base">
              {getPriceDisplay()}
            </span>
            <span className="material-symbols-outlined text-slate-300 text-lg">
              chevron_right
            </span>
          </div>
        </div>
      </Link>
    );
  }

  // Desktop-layout: Finn.no-inspirert boks med bilde og tekst under
  return (
    <Link 
      to={`/annonse/${job.id}`}
      className="block bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md hover:border-slate-200 transition-all group"
    >
      {/* Bilde - 16:9 ratio */}
      <div className="aspect-[4/3] relative overflow-hidden">
        {job.images && job.images.length > 0 ? (
          <img 
            src={job.images[0].startsWith('http') ? job.images[0] : `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${job.images[0]}`} 
            alt={job.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#1E3A5F] to-[#0A1929] flex items-center justify-center">
            <span className="material-symbols-outlined text-white/20 text-6xl">
              {kategori?.icon || 'work'}
            </span>
          </div>
        )}
        
        {/* Type badge - øverst til venstre */}
        {job.job_type && (
          <div className={`absolute top-3 left-3 text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1 ${
            job.job_type === 'tjeneste' 
              ? 'bg-purple-500 text-white' 
              : 'bg-blue-500 text-white'
          }`}>
            <span className="material-symbols-outlined text-sm">
              {job.job_type === 'tjeneste' ? 'handyman' : 'assignment'}
            </span>
            {job.job_type === 'tjeneste' ? 'Tjeneste' : 'Oppdrag'}
          </div>
        )}

        {/* Bilde-teller */}
        {job.images && job.images.length > 1 && (
          <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
            <span className="material-symbols-outlined text-sm">photo_library</span>
            {job.images.length}
          </div>
        )}
      </div>

      {/* Innhold */}
      <div className="p-4">
        {/* Kategori og lokasjon */}
        <div className="flex items-center gap-2 mb-2 text-xs text-slate-500">
          <span className="flex items-center gap-1">
            <span className="material-symbols-outlined text-sm">{kategori?.icon || 'work'}</span>
            {kategori?.name || job.category}
          </span>
          <span>•</span>
          <span className="flex items-center gap-1">
            <span className="material-symbols-outlined text-sm">location_on</span>
            {locationText}
          </span>
        </div>

        {/* Tittel */}
        <h3 className="font-heading font-semibold text-[#0A1929] mb-2 line-clamp-2 group-hover:text-[#1E3A5F] transition-colors">
          {job.title}
        </h3>

        {/* Beskrivelse */}
        <p className="text-slate-500 text-sm mb-3 line-clamp-2">
          {job.description}
        </p>

        {/* Footer med pris */}
        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
          {/* Brukerinfo */}
          {job.poster && (
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#1E3A5F] to-[#0A1929] flex items-center justify-center overflow-hidden">
                {job.poster.profile_image ? (
                  <img src={job.poster.profile_image.startsWith('http') ? job.poster.profile_image : `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${job.poster.profile_image}`} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white text-xs font-bold">
                    {job.poster.name?.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <span className="text-xs text-slate-500 truncate max-w-[80px]">
                {job.poster.name}
              </span>
            </div>
          )}
          
          {/* Pris */}
          <span className="font-heading font-bold text-[#1E3A5F] text-lg">
            {getPriceDisplay()}
          </span>
        </div>
      </div>
    </Link>
  );
};

export default JobCard;
